/* eslint-disable @stylistic/max-statements-per-line, no-nested-ternary, @typescript-eslint/no-unused-expressions */
// pSBC color converter from PimpTrizkit
// https://github.com/PimpTrizkit/PJs/wiki/12.-Shade,-Blend-and-Convert-a-Web-Color-(pSBC.js)
// Version 4.1
/**
 * Input colors as rgb, rgba, or 3, 4, 6, or 8 digit hex. Input a single color and a +/- percentage to lighten or
 * darken the color. Input two colors to blend them together (percentage sets how much of the second color is added).
 * Blending mode defaults to log, but can be set to linear with the 4th parameter.
 * @param {number} p percentage to lighten/darken OR percent to blend towards c1
 * @param {*} c0 color to blend from. Can be hex, rgb, rgba
 * @param {*} [c1] color to blend towards. Can be hex, rgb, rgba
 * @param {*} [l] Blending mode: defaults false for log scale, true for linear scale
 * @return {string}
 */
const pSBC=(p, c0, c1, l) => {
  let r; let g; let b; let P; let f; let t; let h; const m=Math.round; let a=typeof (c1)=='string';
  if (typeof (p)!='number'||p<-1||p>1||typeof (c0)!='string'||(c0[0]!='r'&&c0[0]!='#')||(c1&&!a)) return null;
  h=c0.length>9, h=a?c1.length>9?true:c1=='c'?!h:false:h, f=pSBC.pSBCr(c0), P=p<0, t=c1&&c1!='c'?pSBC.pSBCr(c1):P?{r: 0, g: 0, b: 0, a: -1}:{r: 255, g: 255, b: 255, a: -1}, p=P?p*-1:p, P=1-p;
  if (!f||!t) return null;
  if (l) {
    r=m(P*f.r+p*t.r), g=m(P*f.g+p*t.g), b=m(P*f.b+p*t.b);
  } else {
    r=m((P*f.r**2+p*t.r**2)**0.5), g=m((P*f.g**2+p*t.g**2)**0.5), b=m((P*f.b**2+p*t.b**2)**0.5);
  }
  a=f.a, t=t.a, f=a>=0||t>=0, a=f?a<0?t:t<0?a:a*P+t*p:0;
  if (h) return 'rgb'+(f?'a(':'(')+r+','+g+','+b+(f?','+m(a*1000)/1000:'')+')';
  else {
    return '#'+(4294967296+r*16777216+g*65536+b*256+(f?m(a*255):0)).toString(16).slice(1, f?undefined:-2);
  }
};

pSBC.pSBCr=(d) => {
  const i=parseInt;
  let n=d.length; const x={};
  if (n>9) {
    const [r, g, b, a] = (d = d.split(','));
    n = d.length;
    if (n<3||n>4) return null;
    x.r=i(r[3]=='a'?r.slice(5):r.slice(4)), x.g=i(g), x.b=i(b), x.a=a?parseFloat(a):-1;
  } else {
    if (n==8||n==6||n<4) return null;
    if (n<6) {
      d='#'+d[1]+d[1]+d[2]+d[2]+d[3]+d[3]+(n>4?d[4]+d[4]:'');
    }
    d=i(d.slice(1), 16);
    if (n==9||n==5) {
      x.r=d>>24&255, x.g=d>>16&255, x.b=d>>8&255, x.a=Math.round((d&255)/0.255)/1000;
    } else {
      x.r=d>>16, x.g=d>>8&255, x.b=d&255, x.a=-1;
    }
  } return x;
};

/**
 * Standardize color string to hex/rgba. Works with hex, rgb, rgba, and named colors. Returns black if input is invalid.
 * https://html.spec.whatwg.org/multipage/canvas.html#dom-context-2d-fillstyle
 * @param {string} str color string to standardize
 * @return {string} standardized color string
 */
const standardizeColor = (str) => {
  const ctx = document.createElement('canvas').getContext('2d');
  ctx.fillStyle = str;
  return ctx.fillStyle;
};

/**
 * Converts an RGB color string to ABGR (alpha, blue, green, red)
 * @param {string} rgbColor The RGB color to convert. Can be in hex or rgba(*) format.
 * @param {number} [alpha] The alpha value to use, between 0 and 255. If not provided, use the one from rgbColor.
 * @return {string} A ABGR hex string, without the leading "#".
 */
const rGBtoABGR = (rgbColor, alpha) => {
  if (rgbColor == undefined) return null;
  const {r, g, b, a} = pSBC.pSBCr(rgbColor);
  const toHex = (dec) => (dec).toString(16).padStart(2, '0');
  return [alpha ?? a, b, g, r].map(toHex).join('');
};

/**
 * Converts either a hex color or rgba color to rgba
 * @param {string|null} [rgbColor] The RGB color to convert. Can be in hex or rgba(*) format.
 * @param {number} [opacity] the opacity between 0 and 1
 * @return {string|null} An rgba string, or null when there is no color
 */
const toRGBA = (rgbColor, opacity) => {
  if (rgbColor == undefined) return null;
  const {r, g, b, a} = pSBC.pSBCr(rgbColor);
  // If a is -1, use opacity. If opacity doesn't exist, use 1. If a is not -1, use a
  return `rgba(${r},${g},${b},${a === -1 ? opacity ?? 1 : a})`;
};

/** @typedef {import('@/services/MapService/Map').Point} Point */

/**
 * Returns the first matching pixel in the given radius around a point in an image, and the pixel's coordinates.
 *
 * @param {HTMLImageElement} img
 * @param {Point} point
 * @param {number} radius
 * @param {(data: ImageData) => T} pixelMatcher Must take an ImageData object representing a 1x1 area from a Canvas and
 * return a truthy value if the ImageData matches the desired pixel.
 */
const findPixelInImage = async (img, {x, y} = {}, radius, pixelMatcher) => {
  // validate that img is an HTMLImageElement, x, y, and radius are numbers, and pixelMatcher is a function
  if (img.tagName !== 'IMG' || typeof x !== 'number' || typeof y !== 'number' || typeof radius !== 'number' || typeof pixelMatcher !== 'function') {
    throw new Error('Invalid input');
  }
  if (img.getAttribute('crossOrigin') !== 'Anonymous') {
    const reloaded = new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = (err) => reject(new Error(`${err.message ?? 'Image failed to reload.'}`));
    });
    // Browser security settings do not allow reading data from a canvas that has been 'tainted' by cross-origin data.
    // To avoid this, we set the crossOrigin attribute on the image to 'Anonymous'.
    img.setAttribute('crossOrigin', 'Anonymous');
    // after setting the crossOrigin attribute, the image will reload, so we need to wait for it to load again
    await reloaded;
  }
  const canvas = document.createElement('canvas');
  canvas.style = img.style;
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d', {willReadFrequently: true});
  ctx.drawImage(img, 0, 0);
  const rSquared = radius ** 2;
  let matchedPixel = null;
  for (let col = x - radius; col <= x; col++) { // start searching `radius` pixels to the left
    for (let row = y - radius; row <= y; row++) { // start searching `radius` pixels above
      // a = distance in x, b = distance in y
      // r <= sqrt(a^2 + b^2) == a^2 + b^2 <= r^2
      // r is constant so we can pre-compute r^2 and use a sqrt-less distance check a^2 + b^2 <= rSquared
      const inCircle = (col - x) ** 2 + (row - y) ** 2 <= rSquared; // sqrt-less distance check
      if (!inCircle) continue;
      // The circle is symmetrical, so we can check the other 3 quadrants by reflecting the coordinates instead of
      // recalculating the distance every time.
      const xSym = x - (col - x);
      const ySym = y - (row - y);
      const symPoints = [
        {x: col, y: row}, // top-left
        {x: xSym, y: row}, // top-right
        {x: col, y: ySym}, // bottom-left
        {x: xSym, y: ySym}, // bottom-right
      ];
      for (let j = 0; j < symPoints.length; j++) {
        const {x: checkX, y: checkY} = symPoints[j];
        // the point may not fall on the image if the clicked point is near the edge
        if (checkX < 0 || checkY < 0 || checkX >= img.width || checkY >= img.height) continue;

        const match = pixelMatcher(ctx.getImageData(checkX, checkY, 1, 1));
        if (match) {
          matchedPixel = {x: checkX, y: checkY, match};
          break;
        }
      }
    }
    if (matchedPixel) {
      break;
    }
  }
  return matchedPixel;
};

/**
 * Converts a color string to an array of RGBA values.
 *
 * Supported formats:
 * - Hexadecimal color codes (e.g., `#RGB`, `#RRGGBB`, `#RGBA`, `#RRGGBBAA`)
 * - RGBA strings (e.g., `rgba(r, g, b, a)`)
 *
 * @param {string} color - The color string to convert.
 * @returns {number[] | null} An array of RGBA values `[r, g, b, a]` where `r`, `g`, and `b` are integers (0-255),
 * and `a` is an integer (0-255). Returns `null` if the input is invalid.
 */
const colorToArray = (color) => {
  if (typeof color === 'string' && color?.startsWith('#')) {
    // Remove '#' and convert hex to RGBA
    let hex = color.slice(1);
    if (hex.length === 3) {
      hex = hex.split('').map((c) => c + c).join(''); // Convert #RGB to #RRGGBB
    } else if (hex.length === 4) {
      hex = hex.split('').map((c) => c + c).join(''); // Convert #RGBA to #RRGGBBAA
    }
    let r = parseInt(hex.slice(0, 2), 16);
    let g = parseInt(hex.slice(2, 4), 16);
    let b = parseInt(hex.slice(4, 6), 16);
    let a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) : 255; // Default alpha to 255
    return [r, g, b, a];
  } else if (Array.isArray(color) && color.length === 4) {
    return color;
  } else if (color?.startsWith('rgba')) {
    const match = color.match(/\d+(\.\d+)?/g);
    if (!match) return null;
    let [r, g, b, a] = match.map(Number);
    a = Math.round(a * 255); // Convert alpha from 0-1 to 1-255
    return [r, g, b, a];
  }
  return null;
};

export {
  pSBC,
  standardizeColor,
  rGBtoABGR,
  toRGBA,
  findPixelInImage,
  colorToArray,
};
